import { Duration } from "luxon";
import { NaiveDateTime } from "./NaiveDateTime.js";
import { NaiveDate } from "./NaiveDate.js";
/**
 * NaiveRange offers datetime range comparison.
 *
 * @example
 * // Create a range from 2023-03-01(inclusive) to 2023-03-05(exclusive)
 * const from = NaiveDateTime.fromISO("2023-03-01")
 * const to = NaiveDateTime.fromISO("2023-03-05")
 * const range = new NaiveRange(from, to, "[)")
 */
export class NaiveRange {
    _from;
    _fromInclusive;
    _to;
    _toInclusive;
    static compare(a, comparison, b, inclusive) {
        const av = a.valueOf();
        const bv = b.valueOf();
        if (comparison === ">") {
            return inclusive ? av >= bv : av > bv;
        }
        else {
            return inclusive ? av <= bv : av < bv;
        }
    }
    /**
     * @param from
     * @param to
     * @param inclusivity
     *
     * @example
     * // Create a range from 2023-03-01(inclusive) to 2023-03-05(exclusive)
     * const from = NaiveDateTime.fromISO("2023-03-01")
     * const to = NaiveDateTime.fromISO("2023-03-05")
     * const range = new NaiveRange(from, to, "[)")
     */
    constructor(from, to, inclusivity) {
        if (NaiveDate.isNaiveDate(from)) {
            this._from = NaiveDateTime.fromISO(from.toString());
        }
        else {
            this._from = from;
        }
        if (NaiveDate.isNaiveDate(to)) {
            this._to = NaiveDateTime.fromISO(to.toString());
        }
        else {
            this._to = to;
        }
        const [fromInclusive, toInclusive] = inclusivity;
        this._fromInclusive = fromInclusive === "[";
        this._toInclusive = toInclusive === "]";
    }
    get from() {
        return this._from;
    }
    get to() {
        return this._to;
    }
    get fromInclusive() {
        return this._fromInclusive;
    }
    get toInclusive() {
        return this._toInclusive;
    }
    get inclusivity() {
        return ((this.fromInclusive ? "[" : "(") + (this.toInclusive ? "]" : ")"));
    }
    get isEmpty() {
        return !NaiveRange.compare(this.from, "<", this.to, this.fromInclusive && this.toInclusive);
    }
    equals(range) {
        return (this.from.valueOf() === range.from.valueOf() &&
            this.to.valueOf() === range.to.valueOf() &&
            this.fromInclusive === range.fromInclusive &&
            this.toInclusive === range.toInclusive);
    }
    /**
     * A range to datetime comparison to check if the given datetime is included in the current range.
     *
     * @param dt
     * @returns `dt` ∈ `range`
     *
     * @example
     * // range: 2023-03-01(inclusive) ~ 2023-03-05(exclusive)
     * range.includesDt(NaiveDateTime.fromISO("2023-03-01")) // true
     * range.includesDt(NaiveDateTime.fromISO("2023-03-03")) // true
     * range.includesDt(NaiveDateTime.fromISO("2023-03-05")) // false
     */
    includesDt(dt) {
        return (NaiveRange.compare(dt, ">", this.from, this.fromInclusive) &&
            NaiveRange.compare(dt, "<", this.to, this.toInclusive));
    }
    /**
     * A range to range comparison to check if the given range is included in the current range.
     *
     * @param range
     * @returns `param_range` ⊂ `this_range`
     *
     * @example
     * // range: 2023-03-01(inclusive) ~ 2023-03-05(exclusive)
     * // anotherRange: 2023-03-02(inclusive) ~ 2023-03-04(exclusive)
     * range.includesRange(anotherRange) // true
     *
     * // range: 2023-03-01(inclusive) ~ 2023-03-05(exclusive)
     * // anotherRange: 2023-03-02(inclusive) ~ 2023-03-06(exclusive)
     * range.includesRange(anotherRange) // false
     */
    includesRange(range) {
        if (range.isEmpty)
            return true;
        return (NaiveRange.compare(range.from, ">", this.from, this.fromInclusive || !range.fromInclusive) &&
            NaiveRange.compare(range.to, "<", this.to, this.toInclusive || !range.toInclusive));
    }
    /**
     * A range to range comparison to check if the given range overlaps with the current range.
     *
     * @param range
     * @returns `param_range` ∩ `this_range` ≠ ∅
     *
     * @example
     * // range: 2023-03-01(inclusive) ~ 2023-03-05(exclusive)
     * // anotherRange: 2023-03-02(inclusive) ~ 2023-03-06(exclusive)
     * range.overlaps(anotherRange) // true
     *
     * // range: 2023-03-01(inclusive) ~ 2023-03-05(exclusive)
     * // anotherRange: 2023-03-05(exclusive) ~ 2023-03-07(exclusive)
     * range.overlaps(anotherRange) // false
     */
    overlaps(range) {
        if (this.isEmpty || range.isEmpty)
            return false;
        return (NaiveRange.compare(this.from, "<", range.to, this.fromInclusive && range.toInclusive) &&
            NaiveRange.compare(range.from, "<", this.to, this.toInclusive && range.fromInclusive));
    }
    /**
     * A range to range operation that returns intersection range.
     *
     * @param range
     * @returns `param_range` ∩ `this_range`
     *
     * @example
     * // range: 2023-03-01(inclusive) ~ 2023-03-05(exclusive)
     * // anotherRange: 2023-03-02(inclusive) ~ 2023-03-06(exclusive)
     *
     * // 2023-03-02(inclusive) ~ 2023-03-05(exclusive)
     * range.getIntersection(anotherRange)
     *
     * // range: 2023-03-01(inclusive) ~ 2023-03-05(exclusive)
     * // anotherRange: 2023-03-05(exclusive) ~ 2023-03-07(exclusive)
     *
     * // empty range
     * range.getIntersection(anotherRange)
     */
    getIntersection(range) {
        let from, fromInclusive;
        if (this.from.valueOf() === range.from.valueOf()) {
            from = this.from;
            fromInclusive = this.fromInclusive && range.fromInclusive;
        }
        else {
            ;
            [from, fromInclusive] =
                this.from > range.from ? [this.from, this.fromInclusive] : [range.from, range.fromInclusive];
        }
        let to, toInclusive;
        if (this.to.valueOf() === range.to.valueOf()) {
            to = this.to;
            toInclusive = this.toInclusive && range.toInclusive;
        }
        else {
            ;
            [to, toInclusive] =
                this.to < range.to ? [this.to, this.toInclusive] : [range.to, range.toInclusive];
        }
        const inclusivity = ((fromInclusive ? "[" : "(") +
            (toInclusive ? "]" : ")"));
        return new NaiveRange(from, to, inclusivity);
    }
    static *_createIter(range, interval) {
        const intervalMili = Duration.fromDurationLike(interval).as("milliseconds");
        let dt;
        if (intervalMili === 0)
            return;
        else if (intervalMili < 0) {
            dt = range.toInclusive ? range.to : range.to.plus(interval);
            while (NaiveRange.compare(dt, ">", range.from, range.fromInclusive)) {
                yield dt;
                dt = dt.plus(interval);
            }
        }
        else {
            dt = range.fromInclusive ? range.from : range.from.plus(interval);
            while (NaiveRange.compare(dt, "<", range.to, range.toInclusive)) {
                yield dt;
                dt = dt.plus(interval);
            }
        }
    }
    /**
     * Creates an iterator that iterates over the range with the given interval.
     *
     * @param interval Duration interval of the iteration. If the interval is negative, it will iterate from the end to the start.
     * @example
     * // Iteration in for loop
     * const iter = range.createIter({ days: 1 })
     * for (const dt of iter) {
     *   // do something
     * }
     *
     * // Iteration as an array
     * const arr = [...range.createIter({ days: 1 })]
     */
    createIter(interval) {
        return NaiveRange._createIter(this, interval);
    }
    /**
     * ISO8601
     * 개구간, 폐구간을 고려하지 않는다. 시작 시각은 Inclusive이고 끝 시각은 Exclusive라고 가정한다.
     * TODO: inclusive를 고려해서 출력 하여야 한다.
     */
    toISO() {
        return `${this.from.toISO()}/${this.to.toISO()}`;
    }
}
